import ExcelJS from "exceljs";
import { formatFieldValue } from "./formatFieldValue";

const C = {
  navyBg:   'FF152040',
  pinkBg:   'FFFDF2F2',
  pinkAlt:  'FFF8E8E8',
  green:    'FF10B981',
  gold:     'FFC9A227',
  border:   'FF1E3A5F',
  white:    'FFF8FAFC',
  bodyText: 'FF1E293B',
  tab:      'FF0F1629',
  link:     'FF3B82F6',
  sectionBg:'FFD6DFF0',
  sectionFg:'FF0A1628',
};

function triggerDownload(buffer, filename) {
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function gosFileName(viewName) {
  const now = new Date(), p = (n) => String(n).padStart(2, "0");
  return `GrantOS_${viewName.replace(/\s+/g, "")}_${now.getFullYear()}${p(now.getMonth()+1)}${p(now.getDate())}_${p(now.getHours())}${p(now.getMinutes())}.xlsx`;
}

// 12a: shared object-safe formatter — spreadsheet cells must never
// contain "[object Object]" (arrays of objects like keyDates)
const formatValue = formatFieldValue;

function flattenData(obj, prefix = "") {
  const rows = [];
  if (!obj || typeof obj !== "object") return rows;
  Object.entries(obj).forEach(([k, v]) => {
    if (k.startsWith("_")) return;
    const label = (prefix ? prefix + " › " : "") +
      k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
    if (Array.isArray(v)) { const f = formatValue(v); if (f) rows.push({ label, value: f }); }
    else if (typeof v === "object" && v !== null) rows.push(...flattenData(v, label));
    else { const f = formatValue(v); if (f) rows.push({ label, value: f }); }
  });
  return rows;
}

function groupBySection(data) {
  const groups = [];
  if (!data || typeof data !== "object") return groups;
  Object.entries(data).forEach(([key, value]) => {
    if (key.startsWith("_")) return;
    if (typeof value !== "object" || value === null) return;
    const title = key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
    const rows = flattenData(value);
    if (rows.length > 0) groups.push({ title, rows });
  });
  return groups;
}

function isUrl(str) {
  return typeof str === "string" && /^https?:\/\//i.test(str.trim());
}

function thin(argb) {
  return { style: "thin", color: { argb } };
}

const LINE_H_PT = 14;  // points per wrapped line for 10pt Garamond
const MIN_ROW_H = 16;
const MAX_ROW_H = 409; // Excel hard per-row limit

function autoHeight(value, colWidthChars) {
  if (!value && value !== 0) return MIN_ROW_H;
  const lineCount = String(value).split(/\r?\n/).reduce(
    (sum, l) => sum + Math.max(1, Math.ceil((l.length || 1) / colWidthChars)), 0
  );
  return Math.min(MAX_ROW_H, Math.max(MIN_ROW_H, lineCount * LINE_H_PT));
}

function styleDataRow(row, alt) {
  row.height = autoHeight(row.getCell(2).value, 72);
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = { name: "Garamond", bold: colNumber === 1, size: 10, color: { argb: C.bodyText } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: alt ? C.pinkAlt : C.pinkBg } };
    cell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
    cell.border = { top: thin("FF555555"), bottom: thin("FF555555"), left: thin("FF555555"), right: thin("FF555555") };
  });
}

// ── Extraction tool export ────────────────────────────────────
export async function exportToExcel(data) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "GrantOS";

  const ws = wb.addWorksheet("Grant Extraction");
  ws.properties.tabColor = { argb: C.tab };
  ws.views = [{ showGridLines: false }];
  ws.columns = [{ width: 32 }, { width: 72 }];

  const hdrRow = ws.addRow(["Details", "Information"]);
  hdrRow.height = 22;
  hdrRow.eachCell(cell => {
    cell.font = { name: "Garamond", bold: true, size: 11, color: { argb: C.white } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navyBg } };
    cell.alignment = { horizontal: "left", vertical: "middle" };
    cell.border = { top: thin("FF555555"), bottom: thin("FF555555"), left: thin("FF555555"), right: thin("FF555555") };
  });

  let alt = false;
  if (data._classification?.primary) {
    styleDataRow(ws.addRow(["Grant Type", data._classification.primary]), alt); alt = !alt;
  }
  if (data._confidence) {
    styleDataRow(ws.addRow(["Confidence Level", data._confidence]), alt); alt = !alt;
  }
  ws.addRow(["", ""]);

  groupBySection(data).forEach(group => {
    const sectionRow = ws.addRow([`── ${group.title.toUpperCase()} ──`, ""]);
    sectionRow.height = 18;
    sectionRow.eachCell(cell => {
      cell.font = { name: "Garamond", bold: true, size: 10, color: { argb: C.sectionFg } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.sectionBg } };
      cell.border = { top: thin("FF555555"), bottom: thin("FF555555"), left: thin("FF555555"), right: thin("FF555555") };
    });
    group.rows.forEach(row => { styleDataRow(ws.addRow([row.label, row.value]), alt); alt = !alt; });
    ws.addRow(["", ""]);
  });

  if (data.strategicAnalysis) {
    const stratRows = flattenData(data.strategicAnalysis);
    if (stratRows.length > 0) {
      const ws2 = wb.addWorksheet("Strategic Analysis");
      ws2.properties.tabColor = { argb: C.tab };
      ws2.views = [{ showGridLines: false }];
      ws2.columns = [{ width: 32 }, { width: 72 }];

      const h2 = ws2.addRow(["Dimension", "Analysis"]);
      h2.height = 22;
      h2.eachCell(cell => {
        cell.font = { name: "Garamond", bold: true, size: 11, color: { argb: C.white } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navyBg } };
        cell.alignment = { horizontal: "left", vertical: "middle" };
        cell.border = { top: thin("FF555555"), bottom: thin("FF555555"), left: thin("FF555555"), right: thin("FF555555") };
      });

      let alt2 = false;
      stratRows.forEach(row => { styleDataRow(ws2.addRow([row.label, row.value]), alt2); alt2 = !alt2; });
    }
  }

  const rawName = data.opportunityIdentification?.callTitle
    || data.opportunityIdentification?.callReference
    || data.opportunityIdentification?.funderName
    || "grant";
  const safeName = rawName.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_").slice(0, 60);
  triggerDownload(await wb.xlsx.writeBuffer(), `${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ── Portfolio exports — GrantOS Dark Mode Professional ────────
export async function exportGOSExcel(rows, headers, sheetName, viewName) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "GrantOS";

  const ws = wb.addWorksheet(sheetName.slice(0, 31));
  ws.properties.tabColor = { argb: C.tab };
  ws.views = [{ state: "frozen", xSplit: 0, ySplit: 1, showGridLines: false }];
  // Content-based column widths: scan header labels + first line of each value, cap at 60
  const colWidths = headers.map((h) => {
    const labelLen = String(h.label || '').length;
    const maxLen   = rows.reduce((m, r) => {
      const firstLine = String(r[h.key] ?? '').split(/\r?\n/)[0];
      return Math.max(m, firstLine.length);
    }, labelLen);
    return Math.min(Math.max(maxLen + 2, 10), 60);
  });
  ws.columns = colWidths.map(w => ({ width: w }));

  const hdrRow = ws.addRow(headers.map(h => h.label));
  hdrRow.height = 24;
  hdrRow.eachCell(cell => {
    cell.font = { name: "Garamond", bold: true, size: 11, color: { argb: C.white } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navyBg } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: false };
    cell.border = {
      top:    { style: "double", color: { argb: C.gold } },
      bottom: { style: "double", color: { argb: C.gold } },
      left:   thin(C.border),
      right:  thin(C.border),
    };
  });

  rows.forEach((row, idx) => {
    const isFirst = idx === 0;
    const bgArgb = isFirst ? C.green : (idx % 2 === 1 ? C.pinkAlt : C.pinkBg);
    const dataRow = ws.addRow(headers.map(h => row[h.key] ?? ""));
    dataRow.height = headers.reduce(
      (max, h, i) => Math.max(max, autoHeight(String(row[h.key] ?? ''), colWidths[i])), MIN_ROW_H
    );

    dataRow.eachCell({ includeEmpty: true }, (cell, colIdx) => {
      const raw = String(cell.value ?? "");
      if (isUrl(raw)) {
        cell.value = { text: raw, hyperlink: raw };
        cell.font = { name: "Garamond", size: 10, color: { argb: C.link }, underline: true };
      } else {
        cell.font = {
          name: "Garamond",
          bold: colIdx === 1,
          size: 10,
          color: { argb: isFirst ? C.white : C.bodyText },
        };
      }
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
      cell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
      cell.border = { top: thin(C.border), bottom: thin(C.border), left: thin(C.border), right: thin(C.border) };
    });
  });

  triggerDownload(await wb.xlsx.writeBuffer(), gosFileName(viewName));
}
